import React, { useState } from 'react';
import _ from 'lodash';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import styles from './Ordering.module.scss';
import { onChangePayload } from '../Matching/Matching';

type Item = { id: string; content: string };

type OrderingProps = {
  orderingItems: Item[];
  orderingResponse?: string[];
  disabled: boolean;
  showFeedback: boolean;
  onChange: (arg: onChangePayload) => void;
};

function Ordering({
  orderingItems,
  orderingResponse,
  disabled,
  showFeedback,
  onChange,
}: OrderingProps) {
  function populateInitialItems(items: Item[], response?: string[]) {
    if (!response) return _.shuffle(items);
    // generate items according to responses
    return response.map(
      (id: string) => items.find((item) => item.id === id) as Item
    );
  }
  const [items, setItems] = useState<Item[]>(
    populateInitialItems(orderingItems, orderingResponse)
  );

  function onDragEnd(result: {
    combine?: any;
    destination?: { droppableId: string; index: number };
    draggableId: string;
    mode: 'FLUID' | string;
    reason: 'DROP' | string;
    source: { index: number; droppableId: string };
    type: 'DEFAULT' | string;
  }) {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const reorderedItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );

    setItems(reorderedItems);

    let answeredCorrectly: number = 0;
    reorderedItems.forEach((item, i) => {
      if (item.id === orderingItems[i].id) answeredCorrectly++;
    });

    onChange({
      completed: true,
      score: answeredCorrectly / orderingItems.length,
      response: reorderedItems.map((item) => item.id),
    });
  }

  // a little function to help us with reordering the result
  function reorder(list: Item[], startIndex: number, endIndex: number) {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  }

  function getCorrectOrder(item: Item): number {
    let idx = 0;
    orderingItems.forEach((o, i) => {
      if (o.id === item.id) idx = i;
    });
    return idx;
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className={[
              styles.list,
              snapshot.isDraggingOver ? styles.isDraggingOver : '',
            ].join(' ')}
          >
            {items.map((item, index) => (
              <Draggable
                key={item.id}
                draggableId={item.id}
                index={index}
                isDragDisabled={disabled}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={[
                      styles.item,
                      snapshot.isDragging ? styles.isDragging : '',
                      showFeedback
                        ? index === getCorrectOrder(item)
                          ? styles.isCorrect
                          : styles.isIncorrect
                        : '',
                    ].join(' ')}
                  >
                    {!disabled ? (
                      <FontAwesomeIcon icon={faGripVertical} />
                    ) : null}
                    <p>{item.content}</p>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default Ordering;
